/**
 * RecordsSection — canonical four-folder records grid + import row.
 *
 * Originally inlined in CareReviewConceptScreen as the canonical
 * replacement for the older DocumentsVaultCard. Extracted here so the
 * donor / carrier home (and any other surface that needs a records
 * surface) can render the same canonical design.
 *
 * Consumers pass navigation handlers; this component owns the four
 * fixed categories and their copy.
 */
import { Pressable, View } from "react-native";
import { FolderOpen, Plus } from "lucide-react-native";
import {
  CardSubtitleText,
  CardTitleText,
  Row,
  SectionTitleRow,
} from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import StatusBadge from "../../../components/StatusBadge";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const TEXT_LIGHT = EMBER.colors.textLight;

export type RecordsCategory = "medical" | "coverage" | "legal" | "admin";

/** Maps the RecordsSection category to DocumentsScreen's DocumentCategory key. */
export const RECORDS_CATEGORY_TO_DOC_CATEGORY: Record<RecordsCategory, string> =
  {
    medical: "medical_records",
    coverage: "coverage_benefits",
    legal: "legal_consent",
    admin: "admin",
  };

type CategoryConfig = {
  key: RecordsCategory;
  title: string;
  subtitle: string;
  icon: OrganicMiniIconKind;
};

function useCategories(): CategoryConfig[] {
  const t = useTranslations();
  return [
    {
      key: "medical",
      title: t("care.records.category.medical_title"),
      subtitle: t("care.records.category.medical_subtitle"),
      icon: "medical-records",
    },
    {
      key: "coverage",
      title: t("care.records.category.coverage_title"),
      subtitle: t("care.records.category.coverage_subtitle"),
      icon: "coverage-records",
    },
    {
      key: "legal",
      title: t("care.records.category.legal_title"),
      subtitle: t("care.records.category.legal_subtitle"),
      icon: "legal-records",
    },
    {
      key: "admin",
      title: t("care.records.category.admin_title"),
      subtitle: t("care.records.category.admin_subtitle"),
      icon: "admin-records",
    },
  ];
}

type Props = {
  onOpenCategory: (category: RecordsCategory) => void;
  onOpenImport: () => void;
  onViewAll: () => void;
  /** Per-category badge counts, e.g. `{ medical: "1" }`. */
  categoryCounts?: Partial<Record<RecordsCategory, string>>;
  /** Optional margin above the section title. */
  topMargin?: number;
};

export default function RecordsSection({
  onOpenCategory,
  onOpenImport,
  onViewAll,
  categoryCounts,
  topMargin,
}: Props) {
  const t = useTranslations();
  const [first, second, third, fourth] = useCategories();
  return (
    <View
      style={topMargin !== undefined ? { marginTop: topMargin } : undefined}
    >
      <SectionTitleRow
        title={t("care.records.section_title")}
        icon={FolderOpen}
        action={{ label: t("care.records.view_all"), onPress: onViewAll }}
      />
      <View style={{ gap: 10 }}>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <RecordsFolderCard
            config={first!}
            count={categoryCounts?.[first!.key]}
            onPress={() => onOpenCategory(first!.key)}
          />
          <RecordsFolderCard
            config={second!}
            count={categoryCounts?.[second!.key]}
            onPress={() => onOpenCategory(second!.key)}
          />
        </View>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <RecordsFolderCard
            config={third!}
            count={categoryCounts?.[third!.key]}
            onPress={() => onOpenCategory(third!.key)}
          />
          <RecordsFolderCard
            config={fourth!}
            count={categoryCounts?.[fourth!.key]}
            onPress={() => onOpenCategory(fourth!.key)}
          />
        </View>
        <ImportRecordsCard onPress={onOpenImport} />
      </View>
    </View>
  );
}

function RecordsFolderCard({
  config,
  count,
  onPress,
}: {
  config: CategoryConfig;
  count?: string;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("RecordsFolderCard");
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(config.title),
          `care.records.category.${config.key}_title`,
        );
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={config.title}
      style={({ pressed }) => ({
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        flex: 1,
        minHeight: 118,
        borderRadius: 18,
        padding: 14,
        justifyContent: "space-between",
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View style={{ width: 36, height: 36, justifyContent: "center" }}>
          <OrganicMiniIcon kind={config.icon} size={36} />
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          {count ? <StatusBadge label={count} /> : null}
          <Plus size={19} color={TEXT_LIGHT} strokeWidth={1.7} />
        </View>
      </View>

      <View style={{ minWidth: 0 }}>
        <CardTitleText numberOfLines={1}>{config.title}</CardTitleText>
        <CardSubtitleText numberOfLines={1}>{config.subtitle}</CardSubtitleText>
      </View>
    </Pressable>
  );
}

export function ImportRecordsCard({ onPress }: { onPress: () => void }) {
  const t = useTranslations();
  return (
    <Row
      onPress={onPress}
      action="import_records_card"
      accessibilityRole="button"
      accessibilityLabel={t("care.records.import_title")}
      title={t("care.records.import_title")}
      subtitle={t("care.records.import_subtitle")}
      titleNumberOfLines={1}
      subtitleNumberOfLines={1}
      leading={<OrganicMiniIcon kind="import-records" size={36} />}
      leadingSize={36}
      gap={12}
      borderRadius={18}
      paddingHorizontal={15}
      paddingVertical={14}
    />
  );
}
